"use client";

import dayjs from "dayjs";
import { useSetAtom } from "jotai";
import { getCookie } from "cookies-next";

import { Button } from "@/components/ui/button";

import { getParentsForMultipleCourses } from "@/fetchers/course-consuption";

import useMoengageEventTracker from "@/hooks/use-moengage";
import usePlatformDetector from "@/hooks/use-platform-detector";
import useRangeTimer from "@/hooks/use-range-timer";

import { showOpenInAppModalAtom } from "@/store/modal";

import { convertToSlug } from "@/lib/utils";

function LiveClassAction({
  liveClass,
  onRangeChange,
  onTimeExpire,
  onWindowClose,
}) {
  const trackMoengageEvent = useMoengageEventTracker();
  const { isMobile } = usePlatformDetector();

  const showOpenInAppModal = useSetAtom(showOpenInAppModalAtom);

  const { isInMarginRange, isExpired } = useRangeTimer({
    startTimestamp: liveClass.startTime,
    expiryTimestamp: liveClass.endTime,
    onRangeChange,
    onMarginRangeChange: onRangeChange,
    startTimestampMargin: 600, // Seconds
    endTimestampMargin: 0,
    onTimeExpire,
  });

  const handleLiveClassJoin = () => {
    if (isMobile) {
      showOpenInAppModal(true);
      return;
    }

    const url = new URL(
      `${process.env.NEXT_PUBLIC_FRONTEND_DOMAIN}/live-class/${liveClass.courseId}-${liveClass.externalScheduleId}`
    );

    const userId = getCookie("userID");
    const apiToken = getCookie("apiToken");

    url.pathname = url.pathname.concat("/");

    url.searchParams.append("topic", liveClass.topic);
    url.searchParams.append("name", liveClass.facultyName);
    url.searchParams.append("userId", userId);
    url.searchParams.append("token", apiToken);
    url.searchParams.append("vendor", 1);

    const connectionType = window?.navigator?.connection?.effectiveType;

    try {
      trackMoengageEvent("siq_live_start", {
        external_schedule_id: liveClass.externalScheduleId,
        course_name: liveClass.courseName,
        course_id: liveClass.courseId,
        topic: liveClass.topic,
        teacher_name: liveClass.facultyName,
        start_schedule_time: moment(liveClass.startTime).format("HH:mm"),
        source_screen: "live_class_tab",
        network_type: connectionType,
        city: AuthUserCity || "",
      });
    } catch (error) {
      console.error(error);
    }

    const liveClassWindow = window.open(url.toString(), "_blank");
    if (typeof onWindowClose === "function") {
      const interval = window.setInterval(() => {
        if (!liveClassWindow.closed) return;

        onWindowClose(liveClass);
        window.clearInterval(interval);
      }, 100);

      return () => window.clearInterval(interval);
    }
  };

  const handleRecordedSessionClick = async () => {
    if (isMobile) {
      showOpenInAppModal(true);
      return;
    }

    const courseId = liveClass.courseId;
    const languageId = liveClass.languageId;
    const { lessonId, lessonName } = liveClass.video;

    const response = await getParentsForMultipleCourses([
      { courseId, languageId, lessonId },
    ]);

    const lessonParents =
      response?.data?.[`${liveClass.courseId}-${liveClass.video.lessonId}`];

    if (!lessonParents) return null;

    const videoPath = lessonParents.map((l) => l.id).join("/");

    // Navigate to new page
    window.location.href = `/course-content/${courseId}-${languageId}/${videoPath}/${convertToSlug(lessonName)}`;
  };

  if (liveClass.video) {
    return (
      <Button
        type="button"
        variant="link"
        size="small"
        onClick={handleRecordedSessionClick}
      >
        View recorded session
      </Button>
    );
  }

  if (isExpired && liveClass.status !== 3) {
    return (
      <span className="inline-block h-6">
        <p className="absolute left-0 text-xs text-gray-400 whitespace-nowrap w-full mt-2 text-center">
          Recorded session will be available soon
        </p>
      </span>
    );
  }

  if (
    liveClass.status === 3 &&
    liveClass.isOfflineClassVisibleOnline !== false
  ) {
    return (
      <Button
        type="button"
        variant="link"
        size="small"
        disabled
        onClick={handleLiveClassJoin}
      >
        Join Now
      </Button>
    );
  }

  // If class is rescheduled
  if (
    !isInMarginRange &&
    liveClass.status === 2 &&
    liveClass.isOfflineClassVisibleOnline !== false &&
    dayjs(liveClass.startTime).isSame(dayjs(), "date") === false
  ) {
    return (
      <Button
        type="button"
        variant="link"
        size="small"
        disabled={liveClass.live === false}
        onClick={handleLiveClassJoin}
      >
        Join Now
      </Button>
    );
  }

  if (liveClass.offlineClassMessage && !liveClass.live) {
    return (
      <Button type="button" variant="link" size="small" asChild>
        <span>{liveClass.offlineClassMessage}</span>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="link"
      size="small"
      disabled={liveClass.live === false}
      onClick={handleLiveClassJoin}
    >
      Join Now
    </Button>
  );
}

export default LiveClassAction;
